import { useState, useCallback } from 'react';
import {
  ParameterFormField,
  createEmptyParameter,
  updateNodeInTree,
  addChildToNode,
  removeNodeFromTree,
} from '../utils/tree-utils';
import { SYSTEM_VARIABLE_OPTIONS } from '../utils/system-variable-utils';

export const useParameterTree = (initialParameters: ParameterFormField[]) => {
  const [parameters, setParameters] = useState<ParameterFormField[]>(initialParameters);

  const handleAddParameter = useCallback(() => {
    setParameters((prev) => [
      ...prev,
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        name: '',
        description: '',
        type: 'string',
        location: 'body',
        defaultValue: '',
        required: false,
      },
    ]);
  }, []);

  const handleAddChildParameter = useCallback((parentId: string, parentLocation = 'body') => {
    const newChild: ParameterFormField = {
      id: Date.now().toString() + Math.random().toString(),
      name: '',
      description: '',
      type: 'string',
      location: parentLocation,
      defaultValue: '',
      required: false,
    };
    setParameters((prev) => addChildToNode(prev, parentId, newChild));
  }, []);

  const handleRemoveParameter = useCallback((id: string) => {
    setParameters((prev) => {
      if (prev.length === 1 && prev[0].id === id) {
        return [createEmptyParameter(id)];
      }
      return removeNodeFromTree(prev, id);
    });
  }, []);

  const toggleExpand = useCallback((id: string) => {
    setParameters((prev) =>
      updateNodeInTree(prev, id, (param) => ({
        ...param,
        isExpanded: !param.isExpanded,
      })),
    );
  }, []);

  const handleParameterChange = useCallback(
    (id: string, field: keyof ParameterFormField, value: string | boolean | string[]) => {
      setParameters((prev) =>
        updateNodeInTree(prev, id, (param) => {
          if (field === 'type') {
            const typeValue = value as string;
            const isArray = typeValue === 'array';
            const isObject = typeValue === 'object';

            let newChildren = param.children || [];

            if (isObject) {
              if (!newChildren || newChildren.length === 0) {
                newChildren = [
                  {
                    id: Date.now().toString() + Math.random().toString(),
                    name: '',
                    description: '',
                    type: 'string',
                    location: param.location,
                    defaultValue: '',
                    required: false,
                  },
                ];
              }
              if (newChildren.length === 1 && newChildren[0].name === '[ITEM]') {
                newChildren = [{ ...newChildren[0], name: '' }];
              }
            } else if (isArray) {
              if (!newChildren || newChildren.length === 0 || newChildren.length > 1) {
                newChildren = [
                  {
                    id: Date.now().toString() + Math.random().toString(),
                    name: '[ITEM]',
                    description: '',
                    type: 'string',
                    location: param.location,
                    defaultValue: '',
                    required: false,
                  },
                ];
              } else {
                newChildren = [{ ...newChildren[0], name: '[ITEM]' }];
              }
            } else {
              newChildren = [];
            }

            return {
              ...param,
              type: typeValue,
              isExpanded: isArray || isObject ? true : undefined,
              children: newChildren,
              defaultValue:
                isObject || isArray
                  ? ''
                  : typeValue === 'variable'
                    ? SYSTEM_VARIABLE_OPTIONS[0].value
                    : param.defaultValue,
            };
          }

          return { ...param, [field]: value as any };
        }),
      );
    },
    [],
  );

  return {
    parameters,
    setParameters,
    handleAddParameter,
    handleAddChildParameter,
    handleRemoveParameter,
    handleParameterChange,
    toggleExpand,
  };
};
